import type {
  DealerOptionItemDto,
  MachineAttachmentItemPriceOnlyDto,
  MachineItemPriceOnlyDto
} from '@api/machines'
import {
  calculateDealerOptionsSectionTotal,
  displayOldPrice,
  getDealerFitName,
  matchOldPriceItem,
  Options
} from '@common/utils'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { BreakdownItem } from '../breakdown-item/BreakdownItem'
import { BreakdownSection } from '../breakdown-section/BreakdownSection'
import type { HandleOptionChangeFunctionProps } from '../types'

export type DealerFitOptionsSectionProps = {
  dealerOptions: DealerOptionItemDto[]
  handleOptionChange: HandleOptionChangeFunctionProps
  highlightedSection: boolean
  keyPrefix: string
  onRemoveArchiveItem: ((id: number) => void) | undefined
  quotationItemOldPrices: MachineAttachmentItemPriceOnlyDto &
    MachineItemPriceOnlyDto
  isNonEditableQuote?: boolean
}

const DealerFitOptionsSection = ({
  dealerOptions,
  handleOptionChange,
  highlightedSection,
  keyPrefix,
  onRemoveArchiveItem,
  quotationItemOldPrices,
  isNonEditableQuote = false
}: DealerFitOptionsSectionProps) => {
  const {
    attachmentId,
    equipmentId,
    quoteId: quoteIdParams,
    specId
  } = useParams()

  const quoteId = quoteIdParams ?? specId

  const isWithinMachine = Boolean(equipmentId && attachmentId)

  const [loadingItemId, setLoadingItemId] = useState<number | null>(null)

  // Track previous dealerOptions to clear loading when mutation completes
  const prevDealerOptionsRef = useRef(dealerOptions)
  useEffect(() => {
    if (prevDealerOptionsRef.current !== dealerOptions) {
      prevDealerOptionsRef.current = dealerOptions
      setLoadingItemId(null)
    }
  }, [dealerOptions])

  const fitsWithoutArchived = dealerOptions.filter(item => !item.isArchived)

  const dealerFitOptionsOldPrices = quotationItemOldPrices.dealerOptions
    ?.filter(dealerOption =>
      fitsWithoutArchived.some(
        withoutArchived => withoutArchived.id === dealerOption.id
      )
    )
    .map(dealerOption => {
      const originalItem = fitsWithoutArchived.find(
        withoutArchived => withoutArchived.id === dealerOption.id
      )

      return {
        ...dealerOption,
        dnp: dealerOption.dnp ?? undefined,
        quantity: originalItem?.quantity
      }
    })

  const { t } = useTranslation('', { keyPrefix })

  const sectionTotalCurrentPrice =
    calculateDealerOptionsSectionTotal(fitsWithoutArchived)
  const sectionTotalOldPrice = calculateDealerOptionsSectionTotal(
    dealerFitOptionsOldPrices ?? []
  )

  return fitsWithoutArchived.length > 0 ? (
    <BreakdownSection
      highlightedSection={highlightedSection}
      oldPrice={displayOldPrice(sectionTotalCurrentPrice, sectionTotalOldPrice)}
      price={sectionTotalCurrentPrice}
      title={`${t('dealerFitOptions')} (${fitsWithoutArchived.length})`}
    >
      {fitsWithoutArchived.map((item: DealerOptionItemDto) => {
        const oldPriceWithoutQty = matchOldPriceItem(quotationItemOldPrices, {
          id: item.id ?? 0,
          price: item.dnp ?? 0,
          type: 'dealerOptions'
        })

        const oldPrice =
          oldPriceWithoutQty !== undefined
            ? oldPriceWithoutQty * (item.quantity ?? 0)
            : undefined

        return (
          <BreakdownItem
            onRemove={() => {
              if (item.id) {
                setLoadingItemId(item.id)
                handleOptionChange({
                  machineId: isWithinMachine ? equipmentId : undefined,
                  option: isWithinMachine
                    ? Options.REMOVE_DEALER_FIT_WITHIN_MACHINE
                    : Options.REMOVE_DEALER_FIT,
                  optionId: item.id,
                  quotationId: quoteId as string
                })
                onRemoveArchiveItem?.(item.id)
              }
            }}
            key={`spec_breakdown_dealer_fit_${item.id}`}
            loading={loadingItemId === item.id}
            name={getDealerFitName(item) ?? ''}
            oldPrice={oldPrice}
            price={(item.dnp ?? 0) * (item.quantity ?? 0)}
            removable={true}
            isNonEditableQuote={isNonEditableQuote}
          />
        )
      })}
    </BreakdownSection>
  ) : null
}

export { DealerFitOptionsSection }
